import { vec2, vec3, vec4 } from "gl-matrix";
import { useMemo } from "react";
import { useStore } from "react-redux";

import { useAppDispatch } from "app/redux-store-interactions";
import type { AppDispatch, RootState } from "app/store";
import { renderActions } from "features/render";
import {
    suppressNextCameraHistoryRecording,
    suppressNextOrthoClippingSync,
} from "features/render/hooks/cameraHistorySuppression";
import { CameraState, CameraType, ClippingPlane } from "features/render/types";
import { undoRedoActions } from "features/undoRedo";

import { showSectionPlaneMenuButtonAtPoint } from "./sectionPlaneCreateOrigin";

type AddClippingPlaneWithUndoOptions = {
    screenPoint?: vec2;
};

export function cloneClippingPlanes(planes: ClippingPlane[]): ClippingPlane[] {
    return planes.map((p) => clonePlane(p));
}

function clonePlane(p: ClippingPlane): ClippingPlane {
    return {
        ...p,
        normalOffset: vec4.clone(p.normalOffset),
        anchorPos: p.anchorPos ? vec3.clone(p.anchorPos) : undefined,
        wBounds: p.wBounds ? { ...p.wBounds } : undefined,
        color: [...p.color] as ClippingPlane["color"],
        outline: {
            ...p.outline,
            color: [...p.outline.color] as ClippingPlane["outline"]["color"],
            lineColor: p.outline.lineColor
                ? ([...p.outline.lineColor] as ClippingPlane["outline"]["color"])
                : undefined,
        },
    };
}

function planesAfterDelete(planes: ClippingPlane[], idx: number): ClippingPlane[] {
    return planes
        .filter((_, i) => i !== idx)
        .map((p, i) => (i === 0 ? { ...p, outline: { ...p.outline, enabled: true } } : p));
}

export function addClippingPlaneWithUndo(
    dispatch: AppDispatch,
    getState: () => RootState,
    payload: Omit<ClippingPlane, "color" | "outline">,
    options?: AddClippingPlaneWithUndoOptions,
): number | undefined {
    const before = cloneClippingPlanes(getState().render.clipping.planes);
    dispatch(renderActions.addClippingPlane(payload));
    const after = cloneClippingPlanes(getState().render.clipping.planes);
    if (options?.screenPoint && after.length > before.length) {
        showSectionPlaneMenuButtonAtPoint(after.length - 1, options.screenPoint);
    }
    dispatch(
        undoRedoActions.push({
            description: "Create section plane",
            undoCallback: () => setClippingPlanes(dispatch, before),
            redoCallback: () => {
                setClippingPlanes(dispatch, after);
                if (options?.screenPoint) {
                    showSectionPlaneMenuButtonAtPoint(after.length - 1, options.screenPoint);
                }
            },
        }),
    );
    const created = after.find((plane) => !before.some((prior) => prior.boundsRequestId === plane.boundsRequestId));
    return created?.boundsRequestId;
}

export function flipClippingPlaneWithUndo(dispatch: AppDispatch, index: number) {
    dispatch(renderActions.flipClippingPlane(index));
    const flip = () => dispatch(renderActions.flipClippingPlane(index));
    dispatch(
        undoRedoActions.push({
            description: "Flip section plane",
            undoCallback: flip,
            redoCallback: flip,
        }),
    );
}

export function showClippingPlaneWithUndo(
    dispatch: AppDispatch,
    getState: () => RootState,
    index: number,
    showPlane: boolean,
) {
    const before = cloneClippingPlanes(getState().render.clipping.planes);
    const after = cloneClippingPlanes(before);
    after[index] = { ...after[index], showPlane };
    dispatch(renderActions.setClippingPlanes({ planes: after }));
    dispatch(
        undoRedoActions.push({
            description: "Toggle section plane visibility",
            undoCallback: () => dispatch(renderActions.setClippingPlanes({ planes: before })),
            redoCallback: () => dispatch(renderActions.setClippingPlanes({ planes: after })),
        }),
    );
}

export function deleteClippingPlaneWithUndo(dispatch: AppDispatch, getState: () => RootState, index: number) {
    const before = cloneClippingPlanes(getState().render.clipping.planes);
    const after = cloneClippingPlanes(planesAfterDelete(before, index));
    setClippingPlanes(dispatch, after);
    if (getState().render.camera.type === CameraType.Orthographic) {
        dispatch(renderActions.setClippingInEdit(false));
    }
    dispatch(
        undoRedoActions.push({
            description: "Delete section plane",
            undoCallback: () => setClippingPlanes(dispatch, before),
            redoCallback: () => setClippingPlanes(dispatch, after),
        }),
    );
}

export function finishPlaneOffsetWithUndo(
    dispatch: AppDispatch,
    getState: () => RootState,
    planesBefore: ClippingPlane[],
    planeIndex: number,
    cameraUndo?: { before: CameraState; after: CameraState },
) {
    const planesAfter = getState().render.clipping.planes;
    const beforeP = planesBefore[planeIndex];
    const afterP = planesAfter[planeIndex];
    if (!beforeP || !afterP) {
        return;
    }
    const offsetChanged = Math.abs(beforeP.normalOffset[3] - afterP.normalOffset[3]) >= 0.001;
    const anchorChanged =
        beforeP.anchorPos && afterP.anchorPos && vec3.squaredDistance(beforeP.anchorPos, afterP.anchorPos) >= 1e-6;
    if (!offsetChanged && !anchorChanged) {
        return;
    }

    const before = cloneClippingPlanes(planesBefore);
    const after = cloneClippingPlanes(planesAfter);
    dispatch(
        undoRedoActions.push({
            description: "Move section plane",
            undoCallback: () => {
                dispatch(renderActions.setClippingPlanes({ planes: before }));
                if (cameraUndo) {
                    suppressNextCameraHistoryRecording();
                    suppressNextOrthoClippingSync();
                    dispatch(renderActions.setCamera(cameraUndo.before));
                }
            },
            redoCallback: () => {
                dispatch(renderActions.setClippingPlanes({ planes: after }));
                if (cameraUndo) {
                    suppressNextCameraHistoryRecording();
                    suppressNextOrthoClippingSync();
                    dispatch(renderActions.setCamera(cameraUndo.after));
                }
            },
        }),
    );
}

/** Snapshot the current clipping planes array to capture state before a drag begins. */
export function snapshotClippingPlanes(getState: () => RootState): ClippingPlane[] {
    return cloneClippingPlanes(getState().render.clipping.planes);
}

export function useSectionPlaneUndoRedo() {
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();

    return useMemo(
        () => ({
            addClippingPlaneWithUndo: (
                payload: Omit<ClippingPlane, "color" | "outline">,
                options?: AddClippingPlaneWithUndoOptions,
            ) => addClippingPlaneWithUndo(dispatch, store.getState, payload, options),
            flipClippingPlaneWithUndo: (index: number) => flipClippingPlaneWithUndo(dispatch, index),
            showClippingPlaneWithUndo: (index: number, showPlane: boolean) =>
                showClippingPlaneWithUndo(dispatch, store.getState, index, showPlane),
            deleteClippingPlaneWithUndo: (index: number) =>
                deleteClippingPlaneWithUndo(dispatch, store.getState, index),
            finishPlaneOffsetWithUndo: (
                planesBefore: ClippingPlane[],
                planeIndex: number,
                cameraUndo?: { before: CameraState; after: CameraState },
            ) => finishPlaneOffsetWithUndo(dispatch, store.getState, planesBefore, planeIndex, cameraUndo),
            snapshotPlanes: () => snapshotClippingPlanes(store.getState),
        }),
        [dispatch, store],
    );
}

function setClippingPlanes(dispatch: AppDispatch, planes: ClippingPlane[]) {
    dispatch(
        renderActions.setClippingPlanes(planes.length === 0 ? { planes, enabled: false } : { planes, enabled: true }),
    );
}
